import { z } from 'zod';
/**
 * Telefon (SIP) - a szerver, az admin felulet es a huddle kozos szerzodese.
 *
 * Lasd docs/14-sip.md. Az 1. fazis a BEJOVO hivas: egy kulso szam csenget egy
 * kijelolt kollegánal vagy egy csatornaban, a meglevo huddle-gepezettel.
 */
// ─── Telefonszam ──────────────────────────────────────────────────────────────
/**
 * Egy szam E.164 alakra hozasa (`+3612204836`).
 *
 * A PBX es a felhasznalo sokfele alakban irja ugyanazt: `06 1 220 4836`,
 * `0036...`, `+36-1-...`, `3612204836`. Az osszehasonlitas ezert MINDIG
 * normalizalt alakon megy. Belfoldi `06` elotag = `+36`. Mellek (3-4 jegyu
 * szam) valtozatlanul marad, mert az nem kulso szam.
 */
export function normalizePhoneNumber(raw) {
    const trimmed = raw.trim().replace(/^sip:/i, '').replace(/@.*$/, '');
    const digits = trimmed.replace(/[^\d+]/g, '');
    if (digits.startsWith('+'))
        return `+${digits.slice(1).replace(/\D/g, '')}`;
    if (digits.startsWith('00'))
        return `+${digits.slice(2)}`;
    if (digits.startsWith('06') && digits.length >= 10)
        return `+36${digits.slice(2)}`;
    if (digits.startsWith('36') && digits.length >= 10)
        return `+${digits}`;
    return digits;
}
/** Olvashato alak: `+36 1 220 4836`, `+36 30 123 4567`. Ismeretlen alaknal valtozatlan. */
export function formatPhoneNumber(raw) {
    if (!raw)
        return 'Ismeretlen szám';
    const number = normalizePhoneNumber(raw);
    const hu = /^\+36(1)(\d{3})(\d{4})$/.exec(number) ?? /^\+36(\d{2})(\d{3})(\d{3,4})$/.exec(number);
    if (hu)
        return `+36 ${hu[1]} ${hu[2]} ${hu[3]}`;
    return number;
}
/** Elfogadhato vonalszam: E.164, 8-15 jegy. */
export const phoneNumberSchema = z
    .string()
    .trim()
    .min(3)
    .max(32)
    .transform(normalizePhoneNumber)
    .refine((value) => /^\+\d{8,15}$/.test(value), 'Érvénytelen telefonszám (pl. +3612204836).');
// ─── Vonalak ──────────────────────────────────────────────────────────────────
export const phoneLineTargetSchema = z.enum(['user', 'channel']);
export const phoneLineCandidatesQuery = z.object({ teamId: z.string().min(1).optional() });
export const phoneLineInput = z
    .object({
    /**
     * A ceg, amelyie a vonal. Csak a szuperadmin adhatja meg - a csapat-admin
     * vonala mindig a sajat cegee. A cel es a hivok is ebbol a cegbol valok.
     */
    teamId: z.string().min(1).nullish(),
    number: phoneNumberSchema,
    label: z.string().trim().min(1).max(80),
    target: phoneLineTargetSchema,
    targetUserId: z.string().nullish(),
    targetChannelId: z.string().nullish(),
    isActive: z.boolean().default(true),
    /** A vonal sajat melleke a PBX-en. Ures: a kozos mellek. */
    sipUsername: z
        .string()
        .trim()
        .max(64)
        .regex(/^[A-Za-z0-9._-]*$/, 'A mellék csak betű, szám, pont, kötőjel lehet.')
        .nullish()
        .transform((value) => value || null),
    /** Ures vagy hianyzo = a regi jelszo marad. */
    sipPassword: z.string().max(200).optional(),
    /** Kik hivhatnak kifele errol a vonalrol. */
    callerUserIds: z.array(z.string().min(1)).max(500).default([]),
})
    .refine((value) => (value.target === 'user' ? !!value.targetUserId : !!value.targetChannelId), {
    message: 'A célt (kolléga vagy csatorna) meg kell adni.',
});
// ─── A hivas telefonos aga ────────────────────────────────────────────────────
export const phoneLegStateSchema = z.enum(['ringing', 'active', 'ended']);
export const phoneLegSchema = z.object({
    id: z.string(),
    /** A LiveKit-identitas - a beszed-jelzes es a hangero ezzel kotodik hozza. */
    identity: z.string(),
    direction: z.enum(['inbound', 'outbound']),
    callerNumber: z.string().nullable(),
    calledNumber: z.string().nullable(),
    lineLabel: z.string().nullable(),
    state: phoneLegStateSchema,
    startedAt: z.string(),
    answeredAt: z.string().nullable(),
    answeredBy: z.string().nullable(),
    /** Folyamatban levo atadas: ki adja at kinek. */
    transferFrom: z.string().nullable(),
    transferTo: z.string().nullable(),
    /**
     * A kapcsolodo ProjectFlow-feladat: a ProjectFlow-bol inditott hivasnal es a
     * 30 napon beluli visszahivasnal. A hivasablak linkkent mutatja.
     */
    task: z.object({ title: z.string().nullable(), url: z.string() }).nullable().default(null),
});
/**
 * KI BONTHATJA a telefonos felet.
 *
 * A hivas GAZDAJA: aki felvette, kimenonel aki tarcsazta (`answeredBy`). Ha o
 * mar nincs bent (atadta, kilepett), barki, aki bent ul - kulonben a hivast
 * senki nem tudna letenni.
 *
 * Eles hiba volt: a hivasba bekerult masik kolléga ki akart lepni, a telefonos
 * fel piros gombjat nyomta meg, es a hivott felet bontotta - a hivast indito
 * kolléga orra elott.
 */
export function canHangUpPhoneLeg(leg, joinedUserIds, userId) {
    if (!joinedUserIds.includes(userId))
        return false;
    if (!leg.answeredBy || leg.answeredBy === userId)
        return true;
    return !joinedUserIds.includes(leg.answeredBy);
}
/**
 * Hivhato-e kifele a szam.
 *
 * A szabaly a SZERVEREN dont (a felulet csak elore jelzi): minden magyar szam
 * hivhato, kiveve az emelt dijasokat (06-90, 06-91); kulfold nem. Ez a
 * dijcsalas elleni vedelem: egy ellopott munkamenettel se lehessen draga
 * szamokat hivogatni.
 */
export function checkOutboundNumber(input) {
    const number = normalizePhoneNumber(input);
    if (!number.startsWith('+')) {
        return { ok: false, reason: 'Teljes számot adj meg, pl. 06 30 123 4567.' };
    }
    if (!number.startsWith('+36')) {
        return { ok: false, reason: 'Csak magyarországi számot lehet hívni.' };
    }
    const national = number.slice(3);
    if (!/^\d{8,9}$/.test(national)) {
        return { ok: false, reason: 'Ez nem érvényes magyar telefonszám.' };
    }
    if (/^9[01]/.test(national)) {
        return { ok: false, reason: 'Emelt díjas számot (06-90, 06-91) nem lehet hívni.' };
    }
    return { ok: true, number };
}
/** Hogyan varja a PBX a hivott szamot. */
export const outboundFormats = ['national', 'e164'];
/**
 * A PBX-nek kuldott tarcsazasi szoveg: `06301234567` vagy `+36301234567`,
 * elotte a kivonal-elotaggal, ha a PBX ker ilyet (pl. `0`).
 */
export function toDialString(number, format, prefix = '') {
    const body = format === 'national' ? `06${number.replace(/^\+36/, '')}` : number;
    return `${prefix}${body}`;
}
export const phoneDialInput = z.object({
    number: z.string().trim().min(3).max(40),
    /** Melyik vonalrol (szamrol) hivunk. Hianyzik: a szerver valaszt (lasd dialOut). */
    lineId: z.string().min(1).max(64).optional(),
});
/** Hivashossz: 45 mp, 3:07 perc, 1:02:05 óra. */
export function formatCallDuration(total) {
    if (total < 60)
        return `${total} mp`;
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = String(total % 60).padStart(2, '0');
    return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s} óra` : `${m}:${s} perc`;
}
/**
 * Ures nev ES ures cegnev ES nincs TT-ugyfel = a bejegyzes torlese.
 *
 * Csak a nev kiurítese nem torol: egy szam, amihez cegnev vagy TT-ugyfel
 * tartozik, tovabbra is ertekes - a kolléga csak a szemelyt nem tudja.
 */
export const phoneContactInput = z.object({
    number: phoneNumberSchema,
    name: z.string().trim().max(80),
    companyName: z.string().trim().max(120).optional(),
    ttClinicId: z.string().trim().max(80).optional(),
    /**
     * Salesforce-ugyfelhez kotes (Account Id). `undefined` = nem nyul hozza,
     * ures szoveg = leveszi. Csak AVSlackben marad: a Salesforce-ba nem irunk.
     */
    sfAccountId: z
        .string()
        .trim()
        .regex(/^([a-zA-Z0-9]{15}|[a-zA-Z0-9]{18})?$/)
        .optional(),
});
// ─── TT ugyfelkartya ──────────────────────────────────────────────────────────
/** Egy szam azonositasa: "ki hiv?". */
export const phoneIdentifyQuery = z.object({ number: phoneNumberSchema });
/**
 * Hivasjegyzet a TT-be.
 *
 * A szoveget a KOLLEGA allitja ossze (az atiratbol es a sajat jegyzetebol), es
 * o kuldi el. Felso korlat a TT oldalan nincs (`nvarchar(max)`), de ertelmes
 * hatart huzunk, hogy egy elszabadult felulet ne toltse tele az adatlapot.
 */
export const ttNoteInput = z.object({
    text: z.string().trim().min(1).max(50_000),
    tema: z.string().trim().max(200).optional(),
});
/**
 * Keresés a TT-ben egy ismeretlen szam hozzarendelesehez.
 *
 * Minimum 3 karakter: egy ket betus keresés fel ugyfellistat hozna vissza.
 */
export const phoneTtSearchQuery = z.object({ q: z.string().trim().min(3).max(80) });
/** Egy TT-ugyfel telefonszamai. */
export const phoneTtNumbersQuery = z.object({ clinicId: z.string().trim().min(1).max(80) });
/** Keresés a Salesforce-ban (nev) - minimum 3 karakter, mint a TT-nel. */
export const phoneSfSearchQuery = z.object({ q: z.string().trim().min(3).max(80) });
/**
 * A szam, ahogy az ember latja: nevvel, ha van - `Kiss Anna (+36 30 123 4567)` -,
 * kulonben a formazott szam.
 */
export function phoneDisplay(number, name) {
    const shown = formatPhoneNumber(number);
    return name ? `${name} (${shown})` : shown;
}
export const phoneTransferInput = z.object({ userId: z.string().min(1) });
export const sipSettingsInput = z.object({
    enabled: z.boolean(),
    registrar: z
        .string()
        .trim()
        .min(3)
        .max(200)
        .regex(/^[a-z0-9.-]+(:\d{2,5})?$/i, 'Gépnév és opcionálisan port, pl. pbx.alpha-vet.hu:5060'),
    username: z.string().trim().min(1).max(64),
    /** Ures = a regi jelszo marad. */
    password: z.string().max(200).optional(),
    ringSeconds: z.number().int().min(10).max(120).default(30),
    outboundFormat: z.enum(outboundFormats).default('national'),
    outboundPrefix: z
        .string()
        .trim()
        .max(5)
        .regex(/^[0-9*#]*$/, 'Csak számjegy, * és #.')
        .default(''),
});
//# sourceMappingURL=phone.js.map